import {
  Box,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import CCService from "../../services/httpServiceWithoutSuperAdmin";
import SessionForm from "./SessionForm";

const toDateTimeLocal = (epochMs) => {
  if (!epochMs) return "";
  const date = new Date(epochMs);
  // toISOString gives UTC — remove "Z" and seconds for input compatibility
  const tzOffset = date.getTimezoneOffset() * 60000;
  const localISOTime = new Date(epochMs - tzOffset).toISOString().slice(0, 16);
  return localISOTime;
};

function sortSessionsByStatus(items) {
  const now = Date.now();

  const getStart = (s) =>
    typeof s.start_time === "number"
      ? s.start_time
      : parseInt(s.start_time || "0", 10) || 0;

  const getEnd = (s) =>
    typeof s.end_time === "number"
      ? s.end_time
      : parseInt(s.end_time || "0", 10) || 0;

  const classify = (s) => {
    const start = getStart(s);
    const end = getEnd(s);
    if (start <= now && now <= end) return "ongoing";
    if (start > now) return "upcoming";
    return "ended";
  };

  const groups = { ongoing: [], upcoming: [], ended: [] };

  for (const it of items) {
    const status = classify(it);
    // add status to each object (non-mutating)
    groups[status].push({ ...it, status });
  }

  // sort within each group
  groups.ongoing.sort((a, b) => getEnd(a) - getEnd(b)); // earliest ending first
  groups.upcoming.sort((a, b) => getStart(a) - getStart(b)); // soonest starting first
  groups.ended.sort((a, b) => getEnd(b) - getEnd(a)); // most recently ended first

  return [...groups.ongoing, ...groups.upcoming, ...groups.ended];
}

export default function SessionsPage() {
  const services = new CCService();
  const [sessions, setSessions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [venues, setVenues] = useState([]);
  const [sku, setSku] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [eventType, setEventType] = useState([]);
  const [open, setOpen] = useState(false);
  const [initalData, setInitalData] = useState(null);

  const [bookings, setBookings] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const sessions = await services.get("/ahead/exploreAdmin?limit=10");
        const eventType = await services.get("/ahead/event-types");
        const venues = await services.get("/ahead/admin/event-venues");
        const sku = await services.get("/cartModule/sku?type=Ahead");
        const tickets = await services.get("/ahead/event-ticket-types");
        const organizers = await services.get("/ahead/organizers");

        const payload = sessions?.data ?? sessions;

        let items = [];
        if (Array.isArray(payload?.items)) items = payload.items;
        else if (Array.isArray(payload)) items = payload;
        else items = [];

        setSessions(sortSessionsByStatus(items));

        if (payload?.nextCursor) {
          setNextCursor(payload.nextCursor);
        } else {
          setNextCursor(null);
        }

        const venuesPayload = venues?.data ?? venues;
        if (Array.isArray(venuesPayload?.items)) setVenues(venuesPayload.items);
        else if (Array.isArray(venuesPayload)) setVenues(venuesPayload);
        else setVenues([]);

        const skuPayload = sku?.data ?? sku;
        if (Array.isArray(skuPayload?.items)) setSku(skuPayload.items);
        else if (Array.isArray(skuPayload)) setSku(skuPayload);
        else setSku([]);

        const ticketsPayload = tickets?.data ?? tickets;
        if (Array.isArray(ticketsPayload?.items))
          setTickets(ticketsPayload.items);
        else if (Array.isArray(ticketsPayload)) setTickets(ticketsPayload);
        else setTickets([]);

        const eventTypePayload = eventType?.data ?? eventType;
        if (Array.isArray(eventTypePayload?.items))
          setEventType(eventTypePayload.items);
        else if (Array.isArray(eventTypePayload))
          setEventType(eventTypePayload);
        else setEventType([]);

        const organizersPayload = organizers?.data ?? organizers;
        if (Array.isArray(organizersPayload?.items))
          setOrganizers(organizersPayload.items);
        else if (Array.isArray(organizersPayload))
          setOrganizers(organizersPayload);
        else setOrganizers([]);
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    if (!open) {
      setInitalData(null);
    }
  }, [open]);

  const handleLoadMore = async () => {
    if (!nextCursor || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await services.get(
        `/ahead/exploreAdmin?limit=10&cursor=${nextCursor}`
      );
      const payload = response?.data ?? response;

      let items = [];
      if (Array.isArray(payload?.items)) items = payload.items;
      else if (Array.isArray(payload)) items = payload;
      else items = [];

      setSessions((prevSessions) =>
        sortSessionsByStatus([...prevSessions, ...items])
      );

      if (payload?.nextCursor) {
        setNextCursor(payload.nextCursor);
      } else {
        setNextCursor(null);
      }
    } catch (error) {
      console.error("Error loading more sessions:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const session = await services.get(`/ahead/eventById/${id}`);
      const data = session.item; // note: your API returns { item }

      if (!data) {
        alert("Session not found");
        return;
      }

      // safe parse helper
      const safeParse = (v, fallback) => {
        if (v === null || v === undefined) return fallback;
        if (typeof v === "string") {
          try {
            return JSON.parse(v);
          } catch {
            return fallback;
          }
        }
        return v;
      };

      // parse metadata & tags
      const metadataObj = safeParse(data.metadata, {});
      const tags = Array.isArray(safeParse(data.tags, []))
        ? safeParse(data.tags, [])
        : [];

      // convert metadata to key/value array for the form
      const metadataArray = Object.entries(metadataObj || {}).map(
        ([key, value]) => ({
          key,
          value,
        })
      );

      // parse tickets safely (accept string or array)
      const rawTickets = safeParse(data.tickets, []);
      const tickets = Array.isArray(rawTickets)
        ? rawTickets.map((item) => ({
            // item may already have sku_id or may be the sku row
            sku_id: item.sku_id ?? item.skuId ?? item.skuId ?? "",
            ticket_name: item.name ?? item.ticket_name ?? "",
            capacity: Number(item.capacity || 0),
            // keep id if provided so update flow can use it
            id: item.id ?? null,
          }))
        : [];

      // Parse organizers:
      // Support shapes:
      // 1) data.organizers is a JSON array/string: [{id, role, name, company, avatar, description}, ...]
      // 2) data.organizers is a JSON object with { title, data: [...] } (your metadata-style)
      // 3) metadataObj contains "speaker" or "speakers" as the organizer object
      let organizersPayload = { title: "Organizers", data: [] };

      const rawOrganizers = safeParse(data.organizers, null);
      if (rawOrganizers) {
        // If array => convert to { title, data: [...] } shape
        if (Array.isArray(rawOrganizers)) {
          organizersPayload = {
            title: "Organizers",
            organizer_ids: rawOrganizers.map((o) => o.id),
          };
        } else if (typeof rawOrganizers === "object") {
          // If object has .data (the enriched shape)
          const asData = Array.isArray(rawOrganizers.data)
            ? rawOrganizers.data
            : [];
          organizersPayload = {
            title: rawOrganizers.title ?? "Organizers",
            organizer_ids: asData.map((o) => o.id),
          };
        }
      }

      // Final object prepared for form default values
      const object_data = {
        id: data.id,
        event_id: data.event_id,
        venue_id: data.venue_id,
        name: data.event_name ?? data.name,
        banner: data.banner ?? "",
        description: data.description ?? "",
        start_time: toDateTimeLocal(data.start_time), // your helper
        end_time: toDateTimeLocal(data.end_time),
        tickets,
        tags,
        metadata: JSON.parse(data.metadata),
        // shape expected by SessionForm: { title, data: [ {id, name, avatar, description, role} ] }
        organizers: organizersPayload,
        meta_data: data.seo_meta_data ? JSON.parse(data.seo_meta_data) : null,
      };

      console.log("object_data", object_data);

      setInitalData(object_data);
      setOpen(true);
    } catch (err) {
      console.error("Error loading session for edit:", err);
      alert("Failed to load session details.");
    }
  };

  const handleBookings = async (id) => {
    const bookings = await services.get(`/ahead/bookingDetails/${id}`);

    setBookings(bookings.data);
    setModalOpen(true);
  };

  const styles = {
    page: {
      padding: 16,
      fontFamily:
        "Inter, Roboto, system-ui, -apple-system, 'Segoe UI', 'Helvetica Neue', Arial",
      boxSizing: "border-box",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 16,
    },
    title: { fontSize: 20, fontWeight: 700, margin: 0 },
    newBtn: {
      backgroundColor: "#4F46E5",
      color: "#fff",
      padding: "8px 14px",
      borderRadius: 8,
      border: "none",
      cursor: "pointer",
      fontWeight: 600,
      boxShadow: "0 4px 10px rgba(79,70,229,0.12)",
    },
    tableWrapper: {
      width: "100%",
      overflowX: "auto",
      borderRadius: 10,
      background: "#fff",
      boxShadow: "0 6px 18px rgba(15,23,42,0.06)",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: 720,
    },
    thead: {
      background: "#F3F4F6",
      color: "#4B5563",
      fontSize: 14,
      textAlign: "left",
    },
    th: { padding: "12px 16px", fontWeight: 600 },
    tbodyTr: {
      borderTop: "1px solid rgba(0,0,0,0.06)",
      transition: "background 150ms ease",
    },
    tbodyTrHover: { background: "#FAFAFB" }, // applied via inline event handlers
    td: { padding: "12px 16px", fontSize: 14, color: "#111827" },
    modalWrapper: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      background: "#fff",
      borderRadius: 10,
      padding: 20,
      // width: "90vw",
      // maxWidth: 820,
      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
      outline: "none",
      maxHeight: "90vh",
      overflowY: "auto",
    },
    emptyState: {
      padding: 24,
      textAlign: "center",
      color: "#6B7280",
      fontSize: 14,
    },

    editBtn: {
      padding: "6px 14px",
      borderRadius: "6px",
      border: "none",
      backgroundColor: "#60a5fa", // softer blue
      color: "#fff",
      cursor: "pointer",
      fontWeight: 500,
      fontSize: "0.875rem",
      transition: "background-color 0.2s, transform 0.1s",
      margin: "0 8px",
    },
    deleteBtn: {
      padding: "6px 14px",
      borderRadius: "6px",
      border: "none",
      backgroundColor: "#f87171", // softer red
      color: "#fff",
      cursor: "pointer",
      fontWeight: 500,
      fontSize: "0.875rem",
      transition: "background-color 0.2s, transform 0.1s",
      whiteSpace: "nowrap",
    },
    loadMoreWrapper: {
      display: "flex",
      justifyContent: "center",
      marginTop: 20,
    },
    loadMoreBtn: {
      padding: "10px 20px",
      borderRadius: "8px",
      border: "1px solid #E5E7EB",
      backgroundColor: "#fff",
      color: "#374151",
      cursor: "pointer",
      fontWeight: 500,
      fontSize: "0.875rem",
      transition: "all 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    },
    loadMoreBtnDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  };

  const handleDownloadExcel = () => {
    if (!bookings || bookings.length === 0) return;

    // Format data for Excel
    const formattedData = bookings.map((b) => ({
      Name: `${b.first_name ?? ""} ${b.last_name ?? ""}`.trim() || "Guest",
      Email: b.email_id,
      Reference: b.booking_ref,
      Quantity: b.quantity,
      Amount: Number(b.total_amount).toFixed(2),
    }));

    // Create worksheet and workbook
    const ws = XLSX.utils.json_to_sheet(formattedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");

    // Trigger download
    XLSX.writeFile(wb, "bookings.xlsx");
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={styles.title}>Sessions</h1>
        <button onClick={() => setOpen(true)} style={styles.newBtn}>
          + New Session
        </button>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th style={styles.th}>Event</th>
              <th style={styles.th}>Venue</th>
              <th style={styles.th}>Start</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Action</th>
            </tr>
          </thead>

          <tbody>
            {sessions.length === 0 && (
              <tr>
                <td style={{ ...styles.td }} colSpan={4}>
                  <div style={styles.emptyState}>No sessions found.</div>
                </td>
              </tr>
            )}

            {sessions.map((s) => (
              <tr
                key={s.id}
                style={styles.tbodyTr}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background =
                    styles.tbodyTrHover.background)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <td style={styles.td}>{s.event_name ?? "—"}</td>
                <td style={styles.td}>{s.venue_name ?? "—"}</td>
                <td style={styles.td}>
                  {s.start_time ? new Date(s.start_time).toLocaleString() : "—"}
                </td>
                <td
                  style={{
                    ...styles.td,
                    color:
                      s.status === "ongoing"
                        ? "#4F46E5"
                        : s.status === "upcoming"
                        ? "#FFC107"
                        : "#F44336",
                    textTransform: "uppercase",
                  }}
                >
                  {s.status ?? "—"}
                </td>
                <td style={styles.td}>
                  <button
                    onClick={() => handleEdit(s.id)}
                    style={styles.editBtn}
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => handleBookings(s.id)}
                    style={styles.deleteBtn}
                  >
                    Manage Bookings
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {nextCursor && (
        <div style={styles.loadMoreWrapper}>
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            style={{
              ...styles.loadMoreBtn,
              ...(isLoadingMore ? styles.loadMoreBtnDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isLoadingMore) {
                e.currentTarget.style.backgroundColor = "#F9FAFB";
                e.currentTarget.style.borderColor = "#D1D5DB";
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoadingMore) {
                e.currentTarget.style.backgroundColor = "#fff";
                e.currentTarget.style.borderColor = "#E5E7EB";
              }
            }}
          >
            {isLoadingMore ? "Loading..." : "Load More"}
          </button>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)}>
        <div style={styles.modalWrapper}>
          <SessionForm
            initialData={initalData}
            eventType={eventType}
            venues={venues}
            organizers={organizers}
            sku={sku}
            tickets={tickets}
            onSuccess={() => setOpen(false)}
          />
        </div>
      </Modal>

      {modalOpen && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "90%",
              maxWidth: 1000,
              bgcolor: "background.paper",
              boxShadow: 24,
              borderRadius: 2,
              p: 3,
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            {bookings.length === 0 ? (
              <div className="text-center text-slate-600 py-8">
                No bookings found for this session.
              </div>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Ref</TableCell>
                        <TableCell align="center">Contact</TableCell>
                        <TableCell align="center">Qty</TableCell>
                        <TableCell align="center">Amount</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {bookings.map((b) => (
                        <TableRow
                          key={b.booking_ref}
                          hover
                          sx={{
                            "&:hover": { backgroundColor: "#f3f4f6" },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <TableCell>
                            {`${b.first_name ?? ""} ${
                              b.last_name ?? ""
                            }`.trim() || "Guest"}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "text.secondary",
                              fontSize: "0.875rem",
                            }}
                          >
                            {b.email_id}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.75rem",
                            }}
                          >
                            {b.booking_ref}
                          </TableCell>
                          <TableCell align="center">{b.ph_no}</TableCell>
                          <TableCell align="center">{b.quantity}</TableCell>
                          <TableCell align="center">
                            ₹{Number(b.total_amount).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <button
                    onClick={() => setModalOpen(false)}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "6px",
                      background: "#f3f4f6",
                      border: "none",
                      cursor: "pointer",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#e5e7eb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#f3f4f6")
                    }
                  >
                    Done
                  </button>

                  <button
                    onClick={handleDownloadExcel}
                    style={{
                      padding: "8px 16px",
                      borderRadius: "6px",
                      background: "#22c55e",
                      color: "#fff",
                      border: "none",
                      cursor: "pointer",
                      transition: "background 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#16a34a")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#22c55e")
                    }
                  >
                    Download Excel
                  </button>
                </Box>
              </>
            )}
          </Box>
        </Modal>
      )}
    </div>
  );
}
